import { types, flow, cast } from 'mobx-state-tree';

import QueryString from 'qs';
import * as R from 'ramda';
import * as API from '@src/services/freelancers';
import { ApiStore } from '@src/features/common';
import { Feed as JobsFeedStore } from '@src/features/feed';
import * as FreelancersAPI from '@src/services/freelancers';
import { toCamelCase } from '@src/helpers';
import { toast } from 'src/components';
import { capitalizeFirstLetter } from 'src/helpers';

export const Jobs = types
  .compose(
    ApiStore,
    JobsFeedStore,
    types.model({
      filters: types.optional(types.frozen(), {}),
      published: types.optional(
        types.model({
          list: types.array(types.frozen()),
          page: 1,
          isFetched: false,
          count: 0,
          filters: types.frozen()
        }),
        {
          list: [],
          page: 1,
          count: 0,
          isFetched: false,
          filters: {}
        }
      ),
      notAccepting: types.optional(
        types.model({
          list: types.array(types.frozen()),
          page: 1,
          count: 0,
          isFetched: false,
          filters: types.frozen()
        }),
        {
          list: [],
          page: 1,
          count: 0,
          isFetched: false,
          filters: {}
        }
      ),
      closed: types.optional(
        types.model({
          list: types.array(types.frozen()),
          page: 1,
          count: 0,
          isFetched: false,
          filters: types.frozen()
        }),
        {
          list: [],
          page: 1,
          count: 0,
          isFetched: false,
          filters: {}
        }
      )
    })
  )
  .actions(self => {
    const updateFilters = (newFilters, search, store) => {
      // if search '' that means all all filters just goes straight off
      if (search === '') {
        self[store].filters = self.getEntries(newFilters, () => false);
        return;
      }

      const filterQueryString = QueryString.parse(search, {
        ignoreQueryPrefix: true
      });

      // this will get throught all new accordion filters and mark them checked unchcked
      // depends on fn callback
      self[store].filters = self.getEntries(newFilters, (key, label, item) => {
        return (
          // check if in query string
          Boolean(
            Array.isArray(filterQueryString[key]) &&
            filterQueryString[key].find(v => v === label || Number(v) === item?.id || Number(v) === item?.[0])
          )
        );
      });
    };

    const fetchJobs = flow(function* fetch(
      getJobs: (qs: string) => string,
      search = '',
      options = { blowPrevRes: false }
    ) {
      const { blowPrevRes, store } = options;
      if (!store) return;
      self[store].isFetched = false;
      if (blowPrevRes) {
        self[store].list = cast([]);
        self[store].page = 1;
      }

      const status = {
        published: 'published',
        notAccepting: 'not_accepting_candidates',
        closed: 'closed'
      }[store];

      const res = yield self.fetch(
        getJobs(
          QueryString.stringify(
            {
              statuses: [status],
              page: self[store].page,
              per_page: 20,
              ...QueryString.parse(search, { ignoreQueryPrefix: true })
            },
            { addQueryPrefix: true, arrayFormat: 'brackets' }
          )
        )
      );
      self[store].list = cast(R.uniq([...self[store].list, ...res.entities]));
      self[store].count = res.counters[status];

      self[store].isFetched = true;
      updateFilters(res.filters || {}, search, store);

      if (self.published.isFetched && self.closed.isFetched && self.notAccepting.isFetched) {
        const concatAndUniq = R.pipe(R.concat, R.uniq, R.sortBy(R.prop('label')));

        self.filters = R.mergeWith(
          concatAndUniq,
          R.mergeDeepWith(concatAndUniq, self.published.filters, self.closed.filters),
          self.notAccepting.filters
        );
      } else {
        return {};
      }

      return res;
    });
    return {
      onFetchError: () => { },
      incPage: store => {
        self[store].page = self[store].page + 1;
      },
      /**
       * Remove job from active page and return it to active roles.
       *
       * @remarks
       * This method does not refetch jobs, just delete it from cache.
       *
       */
      removeJob: flow(function* removeJob(status: 'closed' | 'notAccepting' | 'published', id: string | number) {
        try {
          yield self.delete(API.removeJob(id));
          self[status].list = cast(self[status].list.filter(item => item.id !== id));
          self[status].count = self[status].count - 1;

          toast.info('Success');
        } catch (error) {
          toast.info('You cannot delete the job');
        }
      }),
      startSourcing: flow(function* fetch(payload) {
        try {
          yield self.patch<{ job_ids: Array<number> }>(API.addToRole, { job_ids: payload });
          toast.info('Success - This role is now available in the "My Active Jobs" page');
        } catch (error) { }
      }),
      fetchJobs,
      fetchCandidate: flow(function* fetch(jobId, email) {
        const res = yield self.fetch(`/recruiters/jobs/${jobId}/fetch_candidate?` + QueryString.stringify({ email }));
        return res;
      }),
      parseResumes: flow(function* (form: FormData) {
        const data = yield self.post(FreelancersAPI.uploadCv(), form);
        return toCamelCase({ ...data, isChecked: true });
      }),
      addCandidateModalSubmit: flow(function* ({ data: rawData, currentJobId, closeModal, refetchJobs }) {
        try {
          const cvs_attributes = yield Promise.all(
            rawData.map(async item => {
              const { values, resumeUrl } = item;
              const { country, cv_file, custom_answers = [], first_name, last_name, ...rest } = values;

              if (custom_answers?.length) {
                await Promise.all(
                  custom_answers.map(async (anwser, i) => {
                    const index = Object.keys(
                      self.published.list.find(({ id }) => id === currentJobId).required_file_questions
                    )[i];
                    const { file_url } = await self.post(FreelancersAPI.postCustomQuestionAnswer(), anwser);
                    custom_answers[index] = file_url;
                  })
                );
              }

              return {
                remote_document_url: resumeUrl,
                ...(custom_answers.length && { custom_answers: { ...(custom_answers || []) } }),
                country: country?.value || country,
                first_name: capitalizeFirstLetter(first_name || ''),
                last_name: capitalizeFirstLetter(last_name || ''),
                ...rest
              };
            })
          );
          yield self.post(
            FreelancersAPI.addCandidate(currentJobId),
            {
              cvs_attributes
            },
            { onFetchSuccess: ({ messages }) => toast.info(messages.join(' ')) }
          );

          closeModal();

          refetchJobs();
        } catch (error) {
          console.error(error);
        }
      }),
      clearData: store => {
        self[store].isFetched = false;
        self[store].filters = {};
        self[store].page = 1;
        self[store].list = cast([]);
      }
    };
  });
